/** API field configs for student idea editing */

export const SPARK_OVERVIEW_FIELDS = [
  {
    key: "problem_statement",
    label: "Problem statement",
    icon: "lucide:alert-circle",
    rows: 4,
    hint: "Describe the problem you are solving.",
  },
  {
    key: "proposed_solution",
    label: "Proposed solution",
    icon: "lucide:lightbulb",
    rows: 4,
    hint: "Explain your proposed solution.",
  },
  {
    key: "target_audience",
    label: "Target audience",
    icon: "lucide:users",
    rows: 3,
  },
  {
    key: "existing_alternatives",
    label: "Existing alternatives",
    icon: "lucide:git-branch",
    rows: 3,
  },
  {
    key: "primary_impact",
    label: "Primary impact",
    icon: "lucide:zap",
    rows: 2,
  },
];

export const ATTACHMENT_LINK_FIELDS = [
  {
    key: "idea_video_link",
    label: "Idea video link",
    placeholder: "https://...",
    icon: "lucide:video",
  },
  {
    key: "concept_diagram",
    label: "Concept diagram link",
    placeholder: "https://...",
    icon: "lucide:git-branch",
  },
  {
    key: "other_document",
    label: "Other Document link",
    placeholder: "https://...",
    icon: "lucide:file-text",
  },
  {
    key: "idea_image",
    label: "Idea image link",
    placeholder: "https://...",
    icon: "lucide:image",
  },
  {
    key: "pitch_video_link",
    label: "Pitch video link",
    placeholder: "https://...",
    icon: "lucide:clapperboard",
  },
];

export const MARKET_STRATEGY_FIELDS = [
  {
    label: "Ideal Customer Profile",
    field: "ideal_customer_profile",
    urlField: "ideal_customer_profile_url",
  },
  { label: "Value Proposition", field: "value_proposition", urlField: "value_proposition_url" },
  { label: "Product Market Fit", field: "product_market_fit", urlField: "product_market_fit_url" },
  {
    label: "Target Addressable Market",
    field: "target_addressable_market",
    urlField: "target_addressable_market_url",
  },
];

export const EXECUTION_PLAN_FIELDS = [
  { label: "MVP", field: "mvp", urlField: "mvp_url" },
  { label: "Market Validation", field: "market_validation", urlField: "market_validation_url" },
  {
    label: "Financial Projections",
    field: "financial_projections",
    urlField: "financial_projections_url",
  },
  {
    label: "Micro-Grant Proposal",
    field: "micro_grant_proposal",
    urlField: "micro_grant_proposal_url",
  },
];

export const GROWTH_STRATEGY_FIELDS = [
  {
    label: "Enhance Business Model",
    field: "enhance_business_model",
    urlField: "enhance_business_model_url",
  },
  { label: "Product Readiness", field: "product_readiness", urlField: "product_readiness_url" },
  { label: "Customer Profile", field: "customer_profile", urlField: "customer_profile_url" },
  { label: "GTM Strategy", field: "gtm_strategy", urlField: "gtm_strategy_url" },
  { label: "Customer Feedback", field: "customer_feedback", urlField: "customer_feedback_url" },
];

export const buildIdeaFormFromEngagement = (engagement) => {
  if (!engagement) return {};

  const ms = engagement.marketStrategy || {};
  const ep = engagement.executionPlan || {};
  const gs = engagement.growthStrategy || {};
  const pp = engagement.pitchPreparation || {};

  return {
    problem_statement: engagement.problemStatement || "",
    proposed_solution: engagement.proposedSolution || "",
    target_audience: engagement.targetAudience || "",
    existing_alternatives: engagement.existingAlternatives || "",
    primary_impact: engagement.primaryImpact || "",
    idea_video_link: engagement.ideaVideoLink || "",
    concept_diagram: engagement.conceptDiagram || "",
    other_document: engagement.otherDocument || "",
    idea_image: engagement.ideaImage || "",
    pitch_video_link: engagement.pitchVideoLink || "",
    ideal_customer_profile: ms.idealCustomerProfile?.description || "",
    ideal_customer_profile_url: ms.idealCustomerProfile?.file?.url || "",
    value_proposition: ms.valueProposition?.description || "",
    value_proposition_url: ms.valueProposition?.file?.url || "",
    product_market_fit: ms.productMarketFit?.description || "",
    product_market_fit_url: ms.productMarketFit?.file?.url || "",
    target_addressable_market: ms.targetAddressableMarket?.description || "",
    target_addressable_market_url: ms.targetAddressableMarket?.file?.url || "",
    mvp: ep.mvp?.description || "",
    mvp_url: ep.mvp?.file?.url || "",
    market_validation: ep.marketValidation?.description || "",
    market_validation_url: ep.marketValidation?.file?.url || "",
    financial_projections: ep.financialProjections?.description || "",
    financial_projections_url: ep.financialProjections?.file?.url || "",
    micro_grant_proposal: ep.microGrantProposal?.description || "",
    micro_grant_proposal_url: ep.microGrantProposal?.file?.url || "",
    enhance_business_model: gs.enhanceBusinessModel?.description || "",
    enhance_business_model_url: gs.enhanceBusinessModel?.file?.url || "",
    product_readiness: gs.productReadiness?.description || "",
    product_readiness_url: gs.productReadiness?.file?.url || "",
    customer_profile: gs.customerProfile?.description || "",
    customer_profile_url: gs.customerProfile?.file?.url || "",
    gtm_strategy: gs.gtmStrategy?.description || "",
    gtm_strategy_url: gs.gtmStrategy?.file?.url || "",
    customer_feedback: gs.customerFeedback?.description || "",
    customer_feedback_url: gs.customerFeedback?.file?.url || "",
    investor_pitch_problem_statement: pp.investorPitch?.problemStatement || false,
    investor_pitch_product_market_fit: pp.investorPitch?.productMarketFit || false,
    investor_pitch_customer_profile: pp.investorPitch?.customerProfile || false,
    investor_pitch_competitive_landscape: pp.investorPitch?.competitiveLandscape || false,
    investor_pitch_target_addressable_market: pp.investorPitch?.targetAddressableMarket || false,
    market_traction_revenue: pp.marketTraction?.revenue || false,
    market_traction_margin: pp.marketTraction?.margin || false,
    metrics_sales: pp.metrics?.sales || false,
    metrics_pipeline: pp.metrics?.pipeline || false,
    metrics_marketing: pp.metrics?.marketing || false,
    metrics_followers: pp.metrics?.followers || false,
    metrics_customer_acquisition_cost: pp.metrics?.customerAcquisitionCost || false,
    product_video_link: pp.additional?.productVideoLink || "",
  };
};

export const buildPitchPayload = (form) => ({
  investor_pitch_problem_statement: Boolean(form.investor_pitch_problem_statement),
  investor_pitch_product_market_fit: Boolean(form.investor_pitch_product_market_fit),
  investor_pitch_customer_profile: Boolean(form.investor_pitch_customer_profile),
  investor_pitch_competitive_landscape: Boolean(form.investor_pitch_competitive_landscape),
  investor_pitch_target_addressable_market: Boolean(
    form.investor_pitch_target_addressable_market
  ),
  market_traction_revenue: Boolean(form.market_traction_revenue),
  market_traction_margin: Boolean(form.market_traction_margin),
  metrics_sales: Boolean(form.metrics_sales),
  metrics_pipeline: Boolean(form.metrics_pipeline),
  metrics_marketing: Boolean(form.metrics_marketing),
  metrics_followers: Boolean(form.metrics_followers),
  metrics_customer_acquisition_cost: Boolean(form.metrics_customer_acquisition_cost),
  product_video_link: (form.product_video_link || "").trim() || null,
});
